import React, { useCallback, useContext, useRef, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faThumbsUp,
	faTools,
	faPencil,
	faTrash,
	faFlag,
	faMobile,
	faBan, faExclamationTriangle, faReply, faEllipsisH
} from "@fortawesome/free-solid-svg-icons";
import { ReactionBarSelector, ReactionCounter } from '@charkour/react-reactions';
import {ControlledMenu, MenuItem, useHover, useMenuState} from "@szhsin/react-menu";
import axios from 'axios';
import qs from 'qs';
import {API_BASE_URL, API_KEY, boardUrl, reaction, reactionIconSize, reactions} from '../../../config/config';
import { AuthContext } from '../../../context/authContext';
import { convertReactionUsersToReactionData } from '../../../utils/user';
import PopupMenu from "../../common/PopupMenu";

const ActionItems = ({post}) => {
	const actions = [
		{
			title: 'Chỉnh sửa',
			icon: <FontAwesomeIcon icon={faPencil} />,
			url: `${post.view_url}edit`,
			display: post.can_edit
		},
		{
			title: 'Xoá',
			icon: <FontAwesomeIcon icon={faTrash} />,
			url: `${post.view_url}delete`,
			display: post.can_soft_delete && !post.is_deleted
		},
		{
			title: 'Huỷ xoá (Hiển thị lại bài viết)',
			icon: <FontAwesomeIcon icon={faTrash} />,
			url: `${post.view_url}undelete`,
			display: post.is_deleted && post.can_undelete
		},
		{
			title: 'Báo cáo',
			icon: <FontAwesomeIcon icon={faFlag} />,
			url: `${post.view_url}report`,
			display: post.can_report
		},
		{
			title: 'Xem IP đăng bài',
			icon: <FontAwesomeIcon icon={faMobile} />,
			url: `${post.view_url}ip`,
			display: post.can_view_ips
		},
		{
			title: 'Cấm thành viên bình luận tiếp trong bài',
			icon: <FontAwesomeIcon icon={faBan} />,
			url: `${boardUrl}/index.php?threads/${post.thread_id}/reply-bans?username=${post.username}`,
			display: post.can_reply_ban
		},
		{
			title: 'Cảnh cáo',
			icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
			url: `${post.view_url}warn`,
			display: post.can_warn
		}
	];

	return (
		<>
			{actions.map((item, index) => {
				if (item.display) {
					return (
						<MenuItem key={index} href={item.url}>
							<span className="inline-block mr-2">{item.icon}</span>
							<span>{item.title}</span>
						</MenuItem>
					);
				}
			})}
		</>
	);
}

const ActionButtons = ({
    post,
	postId,
    initialReactionsData,
    initialReactionScore,
    isReactedTo,
	canReact = false,
	customApiRoute = null,
	isMini = false
}) => {
	const { visitor } = useContext(AuthContext);

	const [reactedId, setReactedId] = useState(isReactedTo);
	const [reactionScore, setReactionScore] = useState(initialReactionScore);
	const [reactionsData, setReactionsData] = useState(initialReactionsData);

	const ref = useRef(null);
	const [menuState, toggle] = useMenuState({ transition: true });
	const { anchorProps, hoverProps } = useHover(menuState.state, toggle);

	const handleReactionButton = useCallback((reactionId) => {
		reactToPost(reactionId);
	}, [post, visitor.user_id]);

	const apiRoute = customApiRoute ? customApiRoute : 'posts';

	const reactToPost = useCallback((reactionId) => {
		if (!post) {
			return;
		}

		let data = qs.stringify({
			'reaction_id': reactionId
		});

		let config = {
			method: 'post',
			maxBodyLength: Infinity,
			url: `${API_BASE_URL}/${apiRoute}/${postId}/react`,
			headers: {
				'XF-Api-Key': API_KEY,
				'XF-Api-User': visitor.user_id,
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			data: data,
			withCredentials: true
		};

		axios.request(config)
			.then((response) => {
				let action = response.data.action;
				if (action === 'insert') {
					setReactedId(reactionId);
				} else {
					setReactedId(null);
				}
				fetchReactionsData();
			})
			.catch((error) => {
				console.error('Failed to react to post:', error);
			});
	}, [post, visitor.user_id]);

	const fetchReactionsData = useCallback(() => {
		if (!post) {
			return;
		}

		let config = {
			method: 'get',
			maxBodyLength: Infinity,
			url: `${API_BASE_URL}/${apiRoute}/${postId}`,
			headers: {
				'XF-Api-Key': API_KEY,
				'XF-Api-User': visitor.user_id,
			},
			withCredentials: true
		};

		axios.request(config)
			.then((response) => {
				let post = Object.entries(response.data) ? Object.entries(response.data)[0][1] : null;
				if (post && post.reaction_users !== null) {
					setReactionScore(Object.values(post.reaction_users).length);
					setReactionsData(convertReactionUsersToReactionData(Object.values(post.reaction_users)));
				}
			})
			.catch((error) => {
				console.error('Failed to fetch reactions data:', error);
			});
	}, [post, visitor.user_id]);

	if (!post) {
		return null;
	}

	return (
		<div className={`${isMini ? 'text-xs' : 'text-sm'}`}>
			{(visitor && reactionScore > 0) && (
				<div className="mt-4 p-2 border border-borderColor rounded-lg text-white">
					<ReactionCounter
						className={`reaction-counter ${isMini ? 'text-xs' : 'text-sm'}`}
						reactions={reactionsData}
						user={visitor.username}
						showOthersAlways={false}
					/>
				</div>
			)}

			<div className="group mt-4 flex items-center gap-x-4 text-url">
				{(!post.is_first_post) && (
					<a href={post.view_url} className="text-gray-400 hover:text-white">
						{isMini ? post.post_date_meta : post.post_date}
					</a>
				)}

				<div>
					<PopupMenu label={
						<div className="inline-flex items-center gap-x-2 hover:text-white">
							<FontAwesomeIcon icon={faEllipsisH}/>
							<span className="hidden md:inline-block">Công cụ</span>
						</div>
					}>
						<div className="szh-menu-container-background">
							<ActionItems post={post}/>
						</div>
					</PopupMenu>
				</div>

				<div>
					{canReact === true ? (
						<>
							<ControlledMenu {...hoverProps} {...menuState} anchorRef={ref} onClose={() => toggle(false)}
							                className="z-10">
								<div className="reaction-selector">
									<ReactionBarSelector reactions={reactions}
									                     iconSize={isMini ? reactionIconSize * 2/3 : reactionIconSize}
									                     onSelect={handleReactionButton}
									                     showOthersAlways={false}
									/>
								</div>
							</ControlledMenu>

							<div className="reaction-button cursor-pointer inline-flex items-center hover:text-white"
							     ref={ref} {...anchorProps}>
								{reactedId === null ? (
									<FontAwesomeIcon icon={faThumbsUp} className="align-top"/>
								) : (
									<div className="w-[18px] h-[18px]">{reaction(reactedId).node}</div>
								)}

								{reactedId === null ? (
									<span className="ml-1 text-white">{reactionScore}</span>
								) : (
									<b className="ml-1">{reaction(reactedId).label}</b>
								)}
							</div>
						</>
					) : (
						<div className="reaction-button inline-flex items-center hover:text-white">
							<FontAwesomeIcon icon={faThumbsUp} className="align-top"/>
							<span className="ml-1 text-white">{reactionScore}</span>
						</div>
					)}
				</div>

				{post.can_quote && !post.is_first_post && !isMini && (
					<a href={`${boardUrl}/index.php?threads/${post.thread_id}/reply&quote=${post.post_id}`}
					   className="reply-button inline-flex items-center hover:text-white">
						<FontAwesomeIcon icon={faReply} className="align-top"/>
						<span className="ml-1 text-white">Trả lời</span>
					</a>
				)}
			</div>
		</div>
	);
}

export default ActionButtons;
